import { useEffect, useMemo, useState } from "react";
import { InlinePdfView } from "@/components/pdf/InlinePdfView";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { Spinner } from "@/components/ui/Spinner";
import {
  TimesheetFlowFooter,
  TimesheetFlowFooterButton,
} from "@/features/timesheetFlow/components/TimesheetFlowFooter";
import { useTimesheetFlowLayers } from "@/features/timesheetFlow/timesheetFlowLayers";
import {
  createFestpreisLieferschein,
  fetchAuftragLieferscheine,
  fetchHeroAngebotPositionen,
  fetchLieferscheinPreviewPdf,
  fetchLieferscheinQuelle,
  type AuftragLieferscheinSummary,
  type HeroAngebotPosition,
  type LieferscheinCreateBody,
  type LieferscheinQuellePosition,
} from "@/features/timesheetFlow/timesheetFlowApi";
import type { TimesheetProjectPreset } from "@/features/timesheetFlow/timesheetFlowTypes";
import { formatQuantityWithUnit } from "@/lib/documentCalc";
import {
  ExistingLieferscheinAlert,
  ExistingLieferscheinDialog,
} from "@/pages/dokumente/ExistingLieferscheinDialog";
import {
  toExistingLieferscheinHints,
  type ExistingLieferscheinHint,
} from "@/pages/dokumente/existingLieferscheinHint";

type RowState = {
  key: string;
  quelleId: number | null;
  position_typ: string;
  position_nr?: number | null;
  bezeichnung: string;
  beschreibung?: string | null;
  einheit?: string | null;
  menge: number;
  geliefert: number;
  rueckstandEntfernt: boolean;
  editierbar: boolean;
  einzelpreis_netto: number;
  steuersatz_prozent: number;
  manual: boolean;
};

let rowKeySeq = 0;
function nextRowKey(): string {
  rowKeySeq += 1;
  return `ls-row-${rowKeySeq}`;
}

function parseMenge(value: string): number {
  const n = Number(String(value).replace(",", ".").trim());
  return Number.isFinite(n) && n >= 0 ? n : 0;
}

function calcRueckstand(row: RowState): number {
  if (row.rueckstandEntfernt) return 0;
  if (!row.editierbar) return 0;
  return Math.max(0, Number(row.menge || 0) - Number(row.geliefert || 0));
}

function fromQuellePosition(p: LieferscheinQuellePosition): RowState {
  return {
    key: nextRowKey(),
    quelleId: p.id,
    position_typ: p.position_typ || "artikel",
    position_nr: p.position_nr,
    bezeichnung: p.bezeichnung || "",
    beschreibung: p.beschreibung,
    einheit: p.einheit,
    menge: Number(p.menge || 0),
    geliefert: Number(p.menge || 0),
    rueckstandEntfernt: false,
    editierbar: Boolean(p.editierbar),
    einzelpreis_netto: 0,
    steuersatz_prozent: 19,
    manual: false,
  };
}

function emptyManualRow(): RowState {
  return {
    key: nextRowKey(),
    quelleId: null,
    position_typ: "artikel",
    bezeichnung: "",
    menge: 1,
    geliefert: 1,
    rueckstandEntfernt: false,
    editierbar: true,
    einzelpreis_netto: 0,
    steuersatz_prozent: 19,
    manual: true,
  };
}

function fromHeroPosition(p: HeroAngebotPosition): RowState {
  const typ = String(p.position_typ || "artikel").toLowerCase();
  const editierbar = typ === "artikel" || typ === "leistung";
  const menge = editierbar ? Number(p.menge || 1) || 1 : 0;
  return {
    key: nextRowKey(),
    quelleId: null,
    position_typ: typ,
    position_nr: null,
    bezeichnung: p.bezeichnung || "",
    beschreibung: p.beschreibung,
    einheit: p.einheit,
    menge,
    geliefert: menge,
    rueckstandEntfernt: false,
    editierbar,
    einzelpreis_netto: Number(p.einzelpreis_netto || 0) || 0,
    steuersatz_prozent: Number(p.steuersatz_prozent || 19) || 19,
    manual: true,
  };
}

function asPdfBlob(blob: Blob): Blob {
  return blob.type === "application/pdf" ? blob : new Blob([blob], { type: "application/pdf" });
}

function hintsFromSummaries(rows: AuftragLieferscheinSummary[]): ExistingLieferscheinHint[] {
  return toExistingLieferscheinHints(
    rows.map((row) => ({
      id: row.id,
      dokument_typ: "lieferschein",
      status: row.status,
      dokument_nummer: row.dokument_nummer,
      created_at: row.created_at,
    })),
  );
}

export function LieferscheinEditorScreen({
  project,
  onDone,
  onBack,
  onOpenExisting,
}: {
  project: TimesheetProjectPreset;
  onDone: (dokumentId: number) => void;
  onBack: () => void;
  onOpenExisting?: (dokumentId: number) => void;
}) {
  const layers = useTimesheetFlowLayers();
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnungOhneAb, setWarnungOhneAb] = useState(false);
  const [warnungText, setWarnungText] = useState<string | null>(null);
  const [rows, setRows] = useState<RowState[]>([]);
  const [manualMode, setManualMode] = useState(false);
  const [quantitiesIncomplete, setQuantitiesIncomplete] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewBusy, setPreviewBusy] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewBlob, setPreviewBlob] = useState<Blob | null>(null);
  const [existingLieferscheine, setExistingLieferscheine] = useState<ExistingLieferscheinHint[]>(
    [],
  );
  const [duplicateConfirmOpen, setDuplicateConfirmOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setQuantitiesIncomplete(false);
    setExistingLieferscheine([]);
    setDuplicateConfirmOpen(false);
    void fetchAuftragLieferscheine(project.id)
      .then((rows) => {
        if (!cancelled) setExistingLieferscheine(hintsFromSummaries(rows));
      })
      .catch(() => {
        if (!cancelled) setExistingLieferscheine([]);
      });
    void (async () => {
      try {
        const quelle = await fetchLieferscheinQuelle(project.id);
        if (cancelled) return;
        if (!quelle.available) {
          setError(
            "Für diesen Auftrag liegt keine Auftragsbestätigung oder kein Angebot als Quelle vor. Ein Lieferschein kann nicht erstellt werden.",
          );
          setRows([]);
          return;
        }
        setWarnungOhneAb(Boolean(quelle.warnung_ohne_ab));
        setWarnungText(quelle.warnung_text ?? null);
        const appPositions = quelle.positionen || [];
        if (appPositions.length > 0) {
          setManualMode(false);
          setRows(appPositions.map(fromQuellePosition));
          return;
        }

        setManualMode(true);
        if (!quelle.hero_angebot?.hero_document_id) {
          setRows([]);
          return;
        }

        try {
          const data = await fetchHeroAngebotPositionen(project.id);
          if (cancelled) return;
          if (!data.available || !data.positionen?.length) {
            setRows([]);
            setError("Im Hero-Angebot wurden keine Positionen gefunden.");
            return;
          }
          setQuantitiesIncomplete(Boolean(data.quantities_incomplete));
          setRows(data.positionen.map(fromHeroPosition));
        } catch (heroErr: unknown) {
          if (cancelled) return;
          setRows([]);
          setError(
            heroErr instanceof Error
              ? heroErr.message
              : "Hero-Positionen konnten nicht geladen werden.",
          );
        }
      } catch (err: unknown) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Quelle konnte nicht geladen werden.");
          setManualMode(true);
          setRows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project.id]);

  const hasOpenRueckstand = useMemo(
    () => rows.some((r) => calcRueckstand(r) > 0.0005),
    [rows],
  );

  const buildCreateBody = (): LieferscheinCreateBody => {
    if (manualMode) {
      return {
        manuelle_positionen: rows.map((r) => ({
          bezeichnung: r.bezeichnung.trim() || (r.editierbar ? "Position" : "Text"),
          beschreibung: r.beschreibung ?? null,
          einheit: r.einheit ?? null,
          position_typ: r.position_typ || "artikel",
          beauftragt: r.menge,
          geliefert: r.geliefert,
          rueckstand_entfernt: r.rueckstandEntfernt,
          einzelpreis_netto: r.einzelpreis_netto,
          steuersatz_prozent: r.steuersatz_prozent,
        })),
      };
    }
    return {
      positionen: rows
        .filter((r) => r.quelleId != null)
        .map((r) => ({
          quelle_position_id: r.quelleId as number,
          geliefert: r.editierbar ? r.geliefert : 0,
          rueckstand_entfernt: r.rueckstandEntfernt,
        })),
    };
  };

  const validateForSubmit = () => {
    if (!rows.length) {
      throw new Error("Bitte mindestens eine Position behalten oder erfassen.");
    }
    if (manualMode) {
      const editable = rows.filter((r) => {
        const typ = String(r.position_typ || "").toLowerCase();
        return typ === "artikel" || typ === "leistung";
      });
      if (!editable.length) {
        throw new Error("Bitte mindestens eine Position mit Bezeichnung erfassen.");
      }
      for (const row of editable) {
        if (!row.bezeichnung.trim()) {
          throw new Error("Jede Position braucht eine Bezeichnung.");
        }
      }
    }
  };

  const updateGeliefert = (key: string, value: string) => {
    const menge = parseMenge(value);
    setRows((prev) =>
      prev.map((row) =>
        row.key === key ? { ...row, geliefert: menge, rueckstandEntfernt: false } : row,
      ),
    );
  };

  const updateBeauftragt = (key: string, value: string) => {
    const menge = parseMenge(value);
    setRows((prev) =>
      prev.map((row) =>
        row.key === key
          ? {
              ...row,
              menge,
              geliefert: row.geliefert > menge && !row.rueckstandEntfernt ? menge : row.geliefert,
              rueckstandEntfernt: false,
            }
          : row,
      ),
    );
  };

  const updateBezeichnung = (key: string, value: string) => {
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, bezeichnung: value } : row)),
    );
  };

  const clearRueckstand = (key: string) => {
    setRows((prev) =>
      prev.map((row) => (row.key === key ? { ...row, rueckstandEntfernt: true } : row)),
    );
  };

  const addManualRow = () => {
    setRows((prev) => [...prev, emptyManualRow()]);
  };

  const removeRow = (key: string) => {
    setRows((prev) => prev.filter((row) => row.key !== key));
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewError(null);
    setPreviewBlob(null);
  };

  const handlePreview = async () => {
    setPreviewBusy(true);
    setPreviewError(null);
    setError(null);
    try {
      validateForSubmit();
      const blob = await fetchLieferscheinPreviewPdf(project.id, buildCreateBody());
      setPreviewBlob(asPdfBlob(blob));
      setPreviewOpen(true);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Vorschau konnte nicht geladen werden.");
    } finally {
      setPreviewBusy(false);
    }
  };

  const handleSubmit = async (forceNew = false) => {
    setBusy(true);
    setError(null);
    try {
      validateForSubmit();
      if (!forceNew) {
        const existing = hintsFromSummaries(await fetchAuftragLieferscheine(project.id));
        setExistingLieferscheine(existing);
        if (existing.length) {
          setDuplicateConfirmOpen(true);
          return;
        }
      }
      const created = await createFestpreisLieferschein(project.id, buildCreateBody());
      setDuplicateConfirmOpen(false);
      onDone(created.dokument_id);
    } catch (err: unknown) {
      setDuplicateConfirmOpen(false);
      setError(err instanceof Error ? err.message : "Lieferschein konnte nicht erstellt werden.");
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <p className="text-sm text-label-secondary">Lieferschein-Positionen werden geladen …</p>;
  }

  const newestExisting = existingLieferscheine[0];

  return (
    <div className="flex flex-col gap-4">
      <ExistingLieferscheinAlert
        items={existingLieferscheine}
        onOpenNewest={
          onOpenExisting && newestExisting
            ? () => onOpenExisting(newestExisting.id)
            : undefined
        }
      />

      {warnungOhneAb ? (
        <Alert variant="warning" title="Keine Auftragsbestätigung">
          {warnungText ||
            "Bitte kontrollieren Sie genau, was beauftragt wurde – die Positionen stammen aus dem Angebot und wurden noch nicht bestätigt."}
        </Alert>
      ) : null}

      {manualMode && !rows.length ? (
        <Alert variant="info" title="Keine Quellpositionen">
          Es liegt keine Auftragsbestätigung bzw. kein App-Angebot vor. Erfassen Sie die Positionen
          manuell inkl. Geliefert und Rückstand.
        </Alert>
      ) : null}

      {quantitiesIncomplete ? (
        <Alert variant="warning" title="Mengen aus Hero unvollständig">
          Hero liefert für dieses Angebot keine gesicherten Mengen. Bitte Mengen und Geliefert prüfen.
        </Alert>
      ) : null}

      <div className="overflow-x-auto rounded-md border border-separator">
        <table className="w-full min-w-[36rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-separator bg-fill-tertiary/60 text-left text-xs uppercase tracking-wide text-label-secondary">
              <th className="px-2 py-2">Pos.</th>
              <th className="px-2 py-2 text-right">Menge</th>
              <th className="px-2 py-2">Bezeichnung</th>
              <th className="px-2 py-2 text-right">Geliefert</th>
              <th className="px-2 py-2 text-right">Rückstand</th>
              <th className="px-2 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => {
              const typ = String(row.position_typ || "").toLowerCase();
              const rueckstand = calcRueckstand(row);
              if (typ === "titel") {
                return (
                  <tr key={row.key} className="border-b border-separator">
                    <td className="px-2 py-2">{row.position_nr ?? index + 1}</td>
                    <td />
                    <td className="px-2 py-2 font-semibold" colSpan={3}>
                      {row.bezeichnung || "Titel"}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button
                        type="button"
                        className="text-xs text-status-red underline-offset-2 hover:underline"
                        onClick={() => removeRow(row.key)}
                        disabled={busy || previewBusy}
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                );
              }
              if (typ === "text" || typ === "freitext") {
                return (
                  <tr key={row.key} className="border-b border-separator">
                    <td />
                    <td />
                    <td className="px-2 py-2 text-label-secondary whitespace-pre-wrap" colSpan={3}>
                      {row.bezeichnung || row.beschreibung || "Text"}
                    </td>
                    <td className="px-2 py-2 text-right">
                      <button
                        type="button"
                        className="text-xs text-status-red underline-offset-2 hover:underline"
                        onClick={() => removeRow(row.key)}
                        disabled={busy || previewBusy}
                      >
                        Löschen
                      </button>
                    </td>
                  </tr>
                );
              }
              return (
                <tr key={row.key} className="border-b border-separator align-top">
                  <td className="px-2 py-2">{row.position_nr ?? index + 1}</td>
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {manualMode && row.editierbar ? (
                      <input
                        type="number"
                        min={0}
                        step="any"
                        className="w-24 rounded border border-separator bg-card px-2 py-1 text-right"
                        value={Number.isFinite(row.menge) ? String(row.menge) : "0"}
                        onChange={(e) => updateBeauftragt(row.key, e.target.value)}
                        disabled={busy || previewBusy}
                      />
                    ) : (
                      formatQuantityWithUnit(row.menge, row.einheit)
                    )}
                  </td>
                  <td className="px-2 py-2">
                    {manualMode && row.editierbar ? (
                      <>
                        <input
                          type="text"
                          className="w-full min-w-[10rem] rounded border border-separator bg-card px-2 py-1"
                          value={row.bezeichnung}
                          onChange={(e) => updateBezeichnung(row.key, e.target.value)}
                          disabled={busy || previewBusy}
                          placeholder="Bezeichnung"
                        />
                        {row.beschreibung ? (
                          <div className="mt-0.5 text-xs text-label-secondary whitespace-pre-wrap">
                            {row.beschreibung}
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <>
                        <div className="font-medium text-label">{row.bezeichnung || "—"}</div>
                        {row.beschreibung ? (
                          <div className="mt-0.5 text-xs text-label-secondary whitespace-pre-wrap">
                            {row.beschreibung}
                          </div>
                        ) : null}
                      </>
                    )}
                  </td>
                  <td className="px-2 py-2 text-right">
                    {row.editierbar ? (
                      <input
                        type="number"
                        min={0}
                        step="any"
                        className="w-24 rounded border border-separator bg-card px-2 py-1 text-right"
                        value={Number.isFinite(row.geliefert) ? String(row.geliefert) : "0"}
                        onChange={(e) => updateGeliefert(row.key, e.target.value)}
                        disabled={busy || previewBusy}
                      />
                    ) : (
                      formatQuantityWithUnit(row.geliefert, row.einheit)
                    )}
                  </td>
                  <td className="px-2 py-2 text-right whitespace-nowrap">
                    {rueckstand > 0.0005
                      ? formatQuantityWithUnit(rueckstand, row.einheit)
                      : "—"}
                  </td>
                  <td className="px-2 py-2 text-right">
                    <div className="flex flex-col items-end gap-1">
                      {row.editierbar && rueckstand > 0.0005 ? (
                        <button
                          type="button"
                          className="text-xs text-status-red underline-offset-2 hover:underline"
                          onClick={() => clearRueckstand(row.key)}
                          disabled={busy || previewBusy}
                        >
                          Rückstand löschen
                        </button>
                      ) : null}
                      <button
                        type="button"
                        className="text-xs text-status-red underline-offset-2 hover:underline"
                        onClick={() => removeRow(row.key)}
                        disabled={busy || previewBusy}
                      >
                        Löschen
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {manualMode ? (
        <div>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={addManualRow}
            disabled={busy || previewBusy}
          >
            Position hinzufügen
          </Button>
        </div>
      ) : null}

      {!rows.length && !manualMode ? (
        <p className="text-sm text-label-tertiary">Keine Positionen in der Quelle vorhanden.</p>
      ) : null}

      {hasOpenRueckstand ? (
        <p className="text-xs text-label-secondary">
          Mit offenem Rückstand bleibt das Projekt nach dem Speichern offen.
        </p>
      ) : (
        <p className="text-xs text-label-secondary">
          Ohne Rückstand wird das Projekt als fertiggestellt markiert.
        </p>
      )}

      {error ? <p className="text-sm text-status-red">{error}</p> : null}

      <TimesheetFlowFooter>
        <TimesheetFlowFooterButton
          type="button"
          variant="secondary"
          onClick={onBack}
          disabled={busy || previewBusy}
        >
          Zurück
        </TimesheetFlowFooterButton>
        <TimesheetFlowFooterButton
          type="button"
          variant="secondary"
          onClick={() => void handlePreview()}
          disabled={busy || previewBusy || !rows.length}
        >
          {previewBusy ? "Vorschau …" : "Entwurf anschauen"}
        </TimesheetFlowFooterButton>
        <TimesheetFlowFooterButton
          type="button"
          onClick={() => void handleSubmit()}
          disabled={busy || previewBusy || duplicateConfirmOpen || !rows.length}
        >
          Lieferschein erstellen
        </TimesheetFlowFooterButton>
      </TimesheetFlowFooter>

      <ExistingLieferscheinDialog
        open={duplicateConfirmOpen}
        busy={busy}
        items={existingLieferscheine}
        zIndexClass={layers.deep}
        onClose={() => {
          if (busy) return;
          setDuplicateConfirmOpen(false);
        }}
        onContinue={() => void handleSubmit(true)}
        onOpenExisting={
          onOpenExisting
            ? (dokumentId) => {
                setDuplicateConfirmOpen(false);
                onOpenExisting(dokumentId);
              }
            : undefined
        }
      />

      <Dialog
        open={previewOpen}
        onClose={closePreview}
        title="Lieferschein-Entwurf"
        size="3xl"
        zIndexClass={layers.nested}
        fullScreenMobile
        panelClassName="flex max-h-[min(92vh,56rem)] flex-col"
        bodyClassName="flex min-h-0 flex-1 flex-col p-0"
        footer={
          <div className="flex w-full flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="secondary" onClick={closePreview}>
              Schließen
            </Button>
          </div>
        }
      >
        {previewError ? (
          <p className="p-4 text-sm text-status-red">{previewError}</p>
        ) : null}
        {!previewBlob ? (
          <div className="flex flex-1 items-center justify-center py-16">
            <Spinner />
          </div>
        ) : (
          <InlinePdfView
            blob={previewBlob}
            title="Lieferschein-Entwurf PDF"
            className="min-h-[min(70vh,40rem)] w-full flex-1"
          />
        )}
      </Dialog>
    </div>
  );
}
